import React, { useState, useEffect, useContext } from "react";
import { FormProvider } from "react-hook-form";
import { useSiteBulkUpload } from "@hooks";
import { get } from "@utils/axios_api";
import { AuthContext } from "@context/AuthContext";
import { ROLES } from "@constants/roles";

const ExcelUpload = () => {
  const { auth } = useContext(AuthContext);
  const [companyList, setCompanyList] = useState([]);
  const [selectedCompCode, setSelectedCompCode] = useState("");
  const [selectedCompName, setSelectedCompName] = useState("");

  const {
    methods,
    handleFormSubmit,
    previewData,
    loading,
    handleSampleDownload,
    saveData,
  } = useSiteBulkUpload({ compCode: selectedCompCode });

  const { register } = methods;

  // 컴포넌트 마운트시 회사 목록 조회
  useEffect(() => {
    // R2(업체관리자)는 자신의 회사만 사용
    if (auth.role === ROLES.COMP_ADMIN) {
      const myCompany = [{ compCode: auth.compCode, compName: auth.compName }];
      setCompanyList(myCompany);
      setSelectedCompCode(auth.compCode);
      setSelectedCompName(auth.compName);
      return;
    }

    const fetchCompanyList = async () => {
      try {
        const response = await get("/admin/corp/corplist", {
          params: { pageIndex: 1, recordCountPerPage: 1000 }
        });

        if (response && response.resultList) {
          setCompanyList(response.resultList);
        } else {
          setCompanyList([]);
        }
      } catch (error) {
        console.error("회사 목록 조회 오류:", error);
        setCompanyList([]);
      }
    };

    fetchCompanyList();
  }, [auth.role, auth.compCode, auth.compName]);

  const handleCompanySelect = (e) => {
    const selectedValue = e.target.value;
    if (selectedValue) {
      const [compCode, compName] = selectedValue.split("|");
      setSelectedCompCode(compCode);
      setSelectedCompName(compName);
    } else {
      setSelectedCompCode("");
      setSelectedCompName("");
    }
  };

  return (
      <FormProvider {...methods}>
        <div className="contents-wrap">
          <div className="page-title-wrap">
            <h2>
              <span className="icon-dot-circle"></span>
            사업장 일괄 등록[엑셀 등록]
            </h2>
          </div>

          <div className="tab-cont-wrap">
            <div className="tab-cont-01 on">
              <form onSubmit={handleFormSubmit}>
                {/* 안내 및 파일 업로드 영역 */}
                <div className="search-wrap">
                  <table className="table-list" style={{ width: "100%", marginBottom: "20px" }}>
                    <tbody>
                    <tr>
                      <td colSpan="2" style={{ textAlign: "left", padding: "10px" }}>
                        1. 사업장등록 샘플양식을 다운로드 받아 작성한 후 업로드 하십시오.
                      </td>
                    </tr>
                    <tr>
                      <td colSpan="2" style={{ textAlign: "left", padding: "10px" }}>
                        2. 선택된 회사에 속하는 사업장들이 일괄 등록됩니다.
                      </td>
                    </tr>
                    <tr>
                      <td colSpan="2" style={{ textAlign: "left", padding: "10px" }}>
                        3. 업로드 된 사업장 정보를 확인한 후 등록하기 버튼을 클릭하면 사업장 등록이 완료됩니다.
                      </td>
                    </tr>
                    {/* 회사 선택 영역 */}
                    <tr>
                      <th style={{ width: "20%", padding: "10px" }}>회사 선택</th>
                      <td style={{ width: "80%", padding: "10px" }}>
                        <select
                            value={selectedCompCode ? `${selectedCompCode}|${selectedCompName}` : ""}
                            onChange={handleCompanySelect}
                            className="input-customtext"
                            style={{ width: "300px", marginRight: "10px" }}
                            disabled={loading}
                        >
                          <option value="">회사를 선택하세요</option>
                          {companyList.map((company, index) => (
                              <option
                                  key={index}
                                  value={`${company.compCode}|${company.compName}`}
                              >
                                {company.compName}
                              </option>
                          ))}
                        </select>
                        {loading && <span style={{ color: "#666", fontSize: "14px" }}>로딩 중...</span>}
                      </td>
                    </tr>
                    {/* 선택된 회사 정보 표시 */}
                    {selectedCompCode && (
                        <tr>
                          <th style={{ width: "20%", padding: "10px" }}>선택된 회사</th>
                          <td style={{ width: "80%", padding: "10px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
                              <span style={{ fontSize: "16px", fontWeight: "bold", color: "#333" }}>
                                {selectedCompName}
                              </span>
                              <span style={{ fontSize: "14px", color: "#666" }}>
                                (코드: {selectedCompCode})
                              </span>
                            </div>
                          </td>
                        </tr>
                    )}
                    <tr>
                      <th style={{ width: "20%", padding: "10px" }}>엑셀 업로드</th>
                      <td style={{ width: "80%", padding: "10px" }}>
                        <div style={{ display: "flex", alignItems: "center", marginBottom: "8px" }}>
                          <input
                              type="file"
                              id="uploadFile"
                              name="file"
                              className="input-customtext"
                              style={{ marginRight: "10px" }}
                              {...register("file")}
                          />
                        </div>
                        <div style={{ marginBottom: "4px" }}>
                          <small>먼저 샘플양식을 다운로드하세요. 복수 시트는 지원하지 않습니다.</small>
                        </div>
                      </td>
                    </tr>
                    </tbody>
                  </table>

                  {/* 버튼 섹션 */}
                  <div className="btn-right-box" style={{ marginBottom: "20px" }}>
                    <ul>
                      <li>
                        <button
                            type="button"
                            className="btn-excel"
                            onClick={handleSampleDownload}
                            disabled={loading}
                        >
                          샘플양식 다운로드
                        </button>
                      </li>
                      <li>
                        <button
                            type="submit"
                            className="btn-write"
                            id="preview"
                            disabled={loading || !selectedCompCode}
                        >
                          미리보기
                        </button>
                      </li>
                      <li>
                        <button
                            type="button"
                            className="btn-write"
                            id="save"
                            onClick={saveData}
                            disabled={loading || !selectedCompCode}
                        >
                          등록
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* 미리보기 테이블 영역 - 꽉 찬 화면 */}
                <div className="section-tit-wrap">
                  <h2 className="border-left-primary" style={{ marginTop: "10px", marginBottom: "20px" }}>
                    &nbsp;업로드 데이터 미리보기
                    <span className="total-txt">
                    {" "} | 총 <span className="red">{previewData.length || 0}</span> 건
                  </span>
                  </h2>
                </div>
                <div className="table-container-fullscreen">
                  <table className="table-list" style={{ width: "100%" }}>
                    <colgroup>
                      <col style={{ width: "18%" }} />
                      <col style={{ width: "15%" }} />
                      <col style={{ width: "12%" }} />
                      <col style={{ width: "12%" }} />
                      <col style={{ width: "20%" }} />
                      <col style={{ width: "15%" }} />
                      <col style={{ width: "8%" }} />
                    </colgroup>
                    <thead>
                    <tr>
                      <th>사업장명</th>
                      <th>사업자등록번호</th>
                      <th>대표자명</th>
                      <th>전화번호</th>
                      <th>주소</th>
                      <th>상세주소</th>
                      <th>비고</th>
                    </tr>
                    </thead>
                    <tbody>
                    {previewData.length > 0 ? (
                        previewData.map((row, index) => (
                            <tr key={index}>
                              <td>{row.siteName}</td>
                              <td>{row.bizNo}</td>
                              <td>{row.repName}</td>
                              <td>{row.phone}</td>
                              <td>{row.addr1}</td>
                              <td>{row.addr2}</td>
                              <td>{row.notice}</td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                          <td colSpan="7" style={{ textAlign: "center" }}>
                            업로드한 데이터가 없습니다.
                          </td>
                        </tr>
                    )}
                    </tbody>
                  </table>
                </div>
              </form>
            </div>
          </div>
        </div>
      </FormProvider>
  );
};

export { ExcelUpload };